import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlattener, MatTreeFlatDataSource } from '@angular/material/tree';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';

import { AddressModel } from 'src/models/address.model';
import { ItemNode, FlatNode, ChipFilter } from 'src/models/schedule.model';

import { snakeCaseToTitleCase } from 'src/shared/util/snake-to-tile-case';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';


export interface TasksFilter {
  urgency: string[];
  address: string[];
  propertyName: string[];
  type: string[];
}

@Component({
    templateUrl: 'tasks-filter.html',
    styleUrls: ['./tasks-filter.scss'],
    selector: 'tidy-tasks-filter',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class TasksFilterComponent implements OnInit, OnChanges, OnDestroy {

  isMobileBreakpoint: boolean;
  @HostListener('window:resize', ['$event'])
  async onResize(event) {
    this.isMobileBreakpoint = event.target.innerWidth < 711;
  }
  @Input() showLoadingSpinner: any;
  @Input() addresses: AddressModel[];
  @Input() selectedFilters: TasksFilter;
  @Input() issueReportTypes: any;
  @Input() initialPropertyFilter: string[] = [];
  @Input() showAboutThisPage: boolean;
  @Output() searchChange = new EventEmitter<string>();
  @Output() filter = new EventEmitter<{
    selectedFilters: TasksFilter;
    lastSelectedFilter: string;
  }>();
  @Output() addIssue = new EventEmitter<void>();
  @Output() exportCSV = new EventEmitter<void>();
  private _transformer = (node: ItemNode, level: number) => ({
      expandable: !!node.children && node.children.length > 0,
      name: node.name,
      level,
      data: node,
    });
  treeControl = new FlatTreeControl<FlatNode>(
    (node) => node.level,
    (node) => node.expandable
  );
  treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );
  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  form: UntypedFormGroup;
  chips: ChipFilter[] = [];
  filterNamesReferences = {
    'Urgency': 'urgency',
    'Property': 'propertyName',
    Type: 'type',
  };
  pros: any[] = [];


  boundGoToRecurringTasks = this.goToRecurringTasks.bind(this);

constructor(
    private fb: FormBuilder,
    public windowService: WindowService,
    private navCtrl: CustomNavController,
    private storage: TidyStorage,
    private rightSidePanelService: RightSidePanelService,
  ) {
    this.form = this.fb.group({
      addresses: ['', Validators.required],
      search: [''],
    });
    this.form.valueChanges
      .pipe(debounceTime(300))
      .subscribe((val) => this.searchChange.emit(val));
  }

  async ngOnInit(): Promise<void> {
    this.selectedFilters = {
      urgency: [],
      address: [],
      propertyName: this.initialPropertyFilter || [],
      type: [],
    };
    this.isMobileBreakpoint = window.innerWidth < 711;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.addresses && this.addresses?.length > 0) {
      this.getTreeData();
      this.applyInitialPropertyFilter();
    }
  }

  private applyInitialPropertyFilter(): void {
    if (!this.initialPropertyFilter?.length) {
      return;
    }
    this.chips = this.chips.filter(c => c.parentName !== 'propertyName');
    this.selectedFilters.propertyName = [...this.initialPropertyFilter];
    this.initialPropertyFilter.forEach(addressId => {
      const foundAddress = this.addresses.find(addr => addr.id.toString() === addressId);
      if (foundAddress) {
        const chip: ChipFilter = {
          displayParentName: 'Property',
          parentName: 'propertyName',
          name: foundAddress.address_name || foundAddress.address1,
          value: addressId,
          icon: 'home',
          color: foundAddress.color || '#1E2125',
        };
        this.chips.push(chip);
      }
    });
    this.updateTree();
    this.filter.emit({
      selectedFilters: this.selectedFilters,
      lastSelectedFilter: 'propertyName',
    });
  }

  hasChild = (_: number, node: FlatNode) => node.expandable;

  getTreeData(): void {
    const treeData = [
      {
        name: 'Urgency',
        children: [
          { name: 'Low', value: 'low', checked: false },
          { name: 'Normal', value: 'normal', checked: false },
          { name: 'High', value: 'high', checked: false },
          { name: 'Emergency', value: 'emergency', checked: false },
        ],
      },
      {
        name: 'Type',
        children: this.issueReportTypes.map(type => {
          const name = snakeCaseToTitleCase(type.name)
          return { name, value: type.id.toString(), checked: false };
        }),
      },
      {
        name: 'Property',
        children: this.getAddressTree('address_name', 'address1'),
      },
    ];
    treeData.forEach((parent, index) => {
      if (parent.children?.length === 0) {
        treeData.splice(index, 1);
      }
      parent.children?.forEach((child) => {
        child.parentId = index;
      });
    });
    this.dataSource.data = treeData;
  }

  getAddressTree(key: string, keyTwo: string = null): ItemNode[] {
    return this.addresses
      ?.map((address) => ({
          name: address[key] || address[keyTwo],
          value: address.id.toString(),
          checked: false,
        }))
      .filter((address) => address?.name);
  }

  checkNode(node: ItemNode, event: MatCheckboxChange): void {
    node.checked = event.checked;
    this.updateFilters(node, event.checked);
  }

  getNodeVariables(node: ItemNode) {
    const foundParentName = this.dataSource.data[node.data.parentId].name;
    const foundNodeValue = this.dataSource.data[
      node.data.parentId
    ].children.find((child) => child.name === node.name).value;
    const matchedParentName = this.filterNamesReferences[foundParentName];
    return { foundParentName, foundNodeValue, matchedParentName };
  }

  updateFilters(node: ItemNode, isEnabled: boolean): void {
    if (!this.dataSource?.data?.[node?.data?.parentId]?.name) {
      return;
    }
    const { foundParentName, foundNodeValue, matchedParentName } =
      this.getNodeVariables(node);
    const chip: ChipFilter = {
      displayParentName: foundParentName,
      parentName: matchedParentName,
      name: node.name,
      value: foundNodeValue,
      icon: this.getChipIcon(matchedParentName),
      color: this.getChipColor(matchedParentName, foundNodeValue),
    };
    if (isEnabled) {
      this.addChip(chip);
    } else {
      this.removeChip(chip, foundParentName);
    }
  }

  private getChipIcon(parentName: string): string {
    switch (parentName) {
      case 'propertyName':
        return 'home';
      case 'urgency':
        return 'flag';
      case 'type':
        return 'category';
      default:
        return '';
    }
  }

  private getChipColor(parentName: string, value: any): string {
    if (parentName === 'propertyName') {
      const address = this.addresses?.find((a) => a.id.toString() === value);
      return address?.color || '#1E2125';
    }
    return '';
  }

  addChip(chip: ChipFilter): void {
    this.selectedFilters[chip.parentName].push(chip.value);
    this.chips.push(chip);
    this.filter.emit({
      selectedFilters: this.selectedFilters,
      lastSelectedFilter: chip.parentName,
    });
    this.updateTree();
  }

  updateTree(): void {
    this.dataSource.data.forEach((parent) => {
      parent.children.forEach((child) => {
        if (
          this.selectedFilters[
            this.filterNamesReferences[parent.name]
          ].includes(child.value)
        ) {
          child.checked = true;
        } else {
          child.checked = false;
        }
      });
    });
  }

  removeChip(chip: ChipFilter, parentName: string): void {
    this.selectedFilters[chip.parentName] = this.selectedFilters[
      chip.parentName
    ].filter((value) => value !== chip.value);
    this.chips = this.chips.filter(
      (item) => !(item.parentName === chip.parentName && item.value === chip.value)
    );
    this.filter.emit({
      selectedFilters: this.selectedFilters,
      lastSelectedFilter: chip.parentName,
    });
    this.updateTree();
  }

  getNodeChecked(node: ItemNode): boolean {
    if (!this.dataSource?.data?.[node?.data?.parentId]?.name) {
      return;
    }
    const { foundParentName, foundNodeValue, matchedParentName } = this.getNodeVariables(node);
    return this.selectedFilters[matchedParentName].includes(foundNodeValue);
  }

  goToRecurringTasks() {
    this.navCtrl.navigateForward('recurring-maintenance');
  }

  goToArchivedTasks() {
    this.navCtrl.navigateForward('archived-maintenance');
  }

  ngOnDestroy() {
    // Required by OnDestroy interface
  }

}
